import { collection, onSnapshot, orderBy, query, limit, startAfter } from 'firebase/firestore'
import { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { filterValidPO } from 'components/billing/groups/invoice/printable-view/helpers/address'
import { organizeInvoiceLineCharges } from 'components/billing/groups/invoice/printable-view/helpers/invoice'
import { db } from 'providers/WithFirebase'
import {
  resetCreateInvoicesPreviewChargeDrawerState,
  resetCreateInvoicesPreviewData,
  updateCreateInvoicesDocIdToChargeMapItem,
} from 'slices/billing/createInvoicesSlice'
import T from 'T'
import { createDateFromString, formatDateToFEFormatDateFns, isValidDate } from 'utils/date'
import { handleErrorWithMessage } from 'utils/error'
import { getGcsJsonData } from 'utils/file'
import { calculateSum, formatCentsToDollars } from 'utils/price'

import type { AnyAction } from '@reduxjs/toolkit'
import type { Unsubscribe } from 'firebase/firestore'
import type { CreateInvoicesRootState } from 'slices/billing/createInvoicesSliceTypes'
import type {
  InvoicePreviewAddressApi,
  InvoicePreviewApi,
  InvoicePreviewFeeTaxChargeApi,
  InvoicePreviewLineApi,
  InvoicePreviewWorkOrderApi,
} from 'types/api/billing/invoicePreview'

// createDataGridSlice is untyped JS, so the action creators it builds infer as a union with `void`.
const resetChargeDrawerState = resetCreateInvoicesPreviewChargeDrawerState as unknown as () => AnyAction
const resetPreviewData = resetCreateInvoicesPreviewData as unknown as () => AnyAction
const updateDocIdToChargeMap = updateCreateInvoicesDocIdToChargeMapItem as unknown as (payload: {
  docId: string
  value: Record<string, unknown>
}) => AnyAction

/** organizeInvoiceLineCharges decorates each charge with the indent level ChargesTableRow renders. */
type OrganizedFeeTaxCharge = InvoicePreviewFeeTaxChargeApi & { hierarchyLevel?: number }

interface SubChargeItem {
  service: string
  dateRange: string | false | undefined
  startDate: string | false | undefined
  quantity: number | ''
  charge: string
  chargeType?: string
  hierarchyLevel?: number
}

/**
 * One row of the charge accordion. A LocationTax roll-up fills the same shape with the service-line
 * fields left out, so the two kinds sort and render through one code path.
 */
interface ChargeItem {
  service: string
  date: string
  charge: string
  quantity?: number
  total: string
  totalWithSubcharges: number
  subChargeList: SubChargeItem[]
  address: string
  locationId?: string
  workOrders: InvoicePreviewWorkOrderApi[]
  servicePurchaseOrderNumber?: string
  configuredServiceId?: string
  pricedServiceType?: string
  startDate?: string | null
  isLocationTaxRollup?: boolean
}

function getFormattedAddress(address: InvoicePreviewAddressApi | undefined, locationName?: string) {
  const line1 = address?.line1
  const city = address?.city
  const state = address?.state
  const zipCode = address?.zipCode
  const addressLeftStr = [line1, city].filter(Boolean).join(', ')
  const addressRightStr = [state, zipCode].filter(Boolean).join(' ')
  const addressStr = [addressLeftStr, addressRightStr].filter(Boolean).join(', ')
  return [locationName, addressStr].filter(Boolean).join(' | ')
}

const getFormattedDate = (dateStr?: string | null) => {
  if (!dateStr) return ''
  try {
    const date = createDateFromString(dateStr)
    return formatDateToFEFormatDateFns(date)
  } catch (e) {
    return ''
  }
}

const getSubChargeItemFromInvoiceFeeTaxCharges = (
  invoiceFeeTaxCharges: OrganizedFeeTaxCharge,
  pricedServiceType: string
): SubChargeItem => {
  const service = invoiceFeeTaxCharges?.name ?? ''
  const subChargeType = invoiceFeeTaxCharges?.chargeType
  const hierarchyLevel = invoiceFeeTaxCharges?.hierarchyLevel
  const startDate = invoiceFeeTaxCharges?.createdAt
  const formattedStartDate = startDate && isValidDate(startDate) && getFormattedDate(startDate)
  const rangeStartDate = invoiceFeeTaxCharges?.startDate
  const rangeEndDate = invoiceFeeTaxCharges?.endDate
  const formattedRangeDate =
    rangeStartDate &&
    rangeEndDate &&
    isValidDate(rangeStartDate) &&
    isValidDate(rangeEndDate) &&
    `${getFormattedDate(rangeStartDate)} - ${getFormattedDate(rangeEndDate)}`
  const quantity = invoiceFeeTaxCharges?.quantity ?? 0
  const fixedQuantity = quantity < 0 ? '' : quantity
  const charge = invoiceFeeTaxCharges?.feeCharge ?? 0
  const fixedCharge = charge.toFixed(2)
  return {
    service,
    dateRange: formattedRangeDate,
    startDate: pricedServiceType === 'On Request' ? '' : formattedStartDate,
    quantity: subChargeType === 'Disposal' ? '' : fixedQuantity,
    charge: fixedCharge,
    chargeType: subChargeType,
    hierarchyLevel,
  }
}

const getChargeItemFromInvoiceLine = (invoiceLine: InvoicePreviewLineApi): ChargeItem => {
  const addressObj = invoiceLine?.location?.address
  const locationId = invoiceLine?.location?.id
  const locationName = invoiceLine?.location?.locationName
  const address = getFormattedAddress(addressObj, locationName)

  // LocationTax synthetic line → render as a "Taxes" header row at the bottom of the
  // location group, with one indented sub-charge row per tax (the actual tax name +
  // amount). The header row keeps Price/Qty/Amount empty; the breakdown lives in the
  // sub-charges so multiple LOCATION-grouped taxes at one location read clearly.
  const lineType = invoiceLine?.lineType
  if (lineType === 'LocationTax') {
    const lineTaxCharges = invoiceLine?.invoiceFeeTaxCharges ?? []
    const lineSubCharges = lineTaxCharges.map(item => getSubChargeItemFromInvoiceFeeTaxCharges(item, ''))
    // totalWithSubcharges feeds getSubtotalByLocation — the LocationTax amount belongs
    // in the location's subtotal even though the header row's Amount column is blank.
    const lineTotal = invoiceLine?.total ?? 0
    return {
      service: 'Taxes',
      date: '',
      charge: '',
      quantity: undefined,
      total: '',
      totalWithSubcharges: lineTotal,
      subChargeList: lineSubCharges,
      address,
      locationId,
      workOrders: [],
      isLocationTaxRollup: true,
    }
  }

  const service = invoiceLine?.summary ?? ''
  const startDate = invoiceLine?.serviceStartDate ?? null
  const formattedStartDate = getFormattedDate(startDate)
  const endDate = invoiceLine?.serviceEndDate
  const formattedEndDate = getFormattedDate(endDate)
  const charge = invoiceLine?.serviceCharge ?? 0
  const fixedCharge = charge.toFixed(2)
  const quantity = invoiceLine?.quantity
  const totalWithSubcharges = invoiceLine?.total
  const configuredServiceId = invoiceLine?.configuredServiceId
  const invoiceFeeTaxCharges = invoiceLine?.invoiceFeeTaxCharges ?? []
  const pricedServiceType = invoiceLine?.pricedServiceType ?? ''
  const subChargeList = invoiceFeeTaxCharges.map(item => getSubChargeItemFromInvoiceFeeTaxCharges(item, pricedServiceType))
  const workOrders = invoiceLine?.workOrders ?? []
  const servicePurchaseOrderNumber = invoiceLine?.servicePurchaseOrderNumber
  return {
    service,
    date: [formattedStartDate, formattedEndDate].filter(Boolean).join(' - '),
    charge: fixedCharge,
    quantity,
    total: fixedCharge,
    totalWithSubcharges,
    subChargeList,
    configuredServiceId,
    address,
    pricedServiceType,
    startDate,
    locationId,
    workOrders,
    servicePurchaseOrderNumber,
  }
}

const compareInvoiceLine = (a: ChargeItem, b: ChargeItem) => {
  // LocationTax roll-ups always sort to the END of the location group, so they
  // appear under the service lines they aggregate.
  if (a.isLocationTaxRollup && !b.isLocationTaxRollup) return 1
  if (!a.isLocationTaxRollup && b.isLocationTaxRollup) return -1

  const aPricedServiceType = a.pricedServiceType ?? ''
  const bPricedServiceType = b.pricedServiceType ?? ''
  if (aPricedServiceType < bPricedServiceType) {
    return -1
  }
  if (aPricedServiceType > bPricedServiceType) {
    return 1
  }
  if (a.service < b.service) {
    return -1
  }
  if (a.service > b.service) {
    return 1
  }
  return 0
}

const getChargeItemDedupKey = (chargeItem: ChargeItem) => {
  const summary = chargeItem.service || ''
  const configuredServiceId = chargeItem.configuredServiceId || ''
  const woKey = (chargeItem.workOrders || []).map(w => w.workOrderNumber).join(',')
  return `${configuredServiceId}|${summary}|${woKey}`
}

// On-Request lines bypass dedup so they cannot mask later recurring lines that share the same key.
const updateLocationIdToChargeObjList = (
  locationIdToChargeObjList: Record<string, ChargeItem[]>,
  seenKeysByLocation: Record<string, Set<string>>,
  locationId: string,
  chargeItemToBeInserted: ChargeItem
) => {
  const isOnRequestService = chargeItemToBeInserted.pricedServiceType === 'On Request'
  // LocationTax roll-ups skip dedup — multiple LOCATION-grouped taxes on the same
  // location each get their own row; their dedup key would collide because the
  // synthetic lines share configuredServiceId="" and have no workOrders.
  const skipDedup = isOnRequestService || chargeItemToBeInserted.isLocationTaxRollup
  let seen = seenKeysByLocation[locationId]
  if (!seen) {
    seen = new Set()
    // eslint-disable-next-line no-param-reassign
    seenKeysByLocation[locationId] = seen
  }
  if (!skipDedup) {
    const newKey = getChargeItemDedupKey(chargeItemToBeInserted)
    if (seen.has(newKey)) return
    seen.add(newKey)
  }
  // Multiple LOCATION-grouped taxes at one location come in as separate LocationTax
  // synthetic lines. Merge them into a single "Taxes" header rollup so the location
  // group renders one header with all taxes as sub-charges — one header per location,
  // not one per tax.
  if (chargeItemToBeInserted.isLocationTaxRollup) {
    const existingRollup = (locationIdToChargeObjList[locationId] || []).find(item => item.isLocationTaxRollup)
    if (existingRollup) {
      existingRollup.subChargeList = [...existingRollup.subChargeList, ...chargeItemToBeInserted.subChargeList]
      existingRollup.totalWithSubcharges = (existingRollup.totalWithSubcharges || 0) + (chargeItemToBeInserted.totalWithSubcharges || 0)
      return
    }
  }
  if (!(locationId in locationIdToChargeObjList)) {
    // eslint-disable-next-line no-param-reassign
    locationIdToChargeObjList[locationId] = [chargeItemToBeInserted]
    return
  }
  const invoiceLines = locationIdToChargeObjList[locationId]
  const index = invoiceLines.findIndex(invoiceLine => compareInvoiceLine(invoiceLine, chargeItemToBeInserted) === 1)
  if (index === -1) {
    invoiceLines.push(chargeItemToBeInserted)
  } else {
    invoiceLines.splice(index, 0, chargeItemToBeInserted)
  }
}

const getLocationIdToChargeList = (data: InvoicePreviewApi) => {
  const locationIdToChargeObj: Record<string, ChargeItem[]> = {}
  const seenKeysByLocation: Record<string, Set<string>> = {}
  const invoiceLines = data?.invoiceLines ?? []
  // LocationTax synthetic lines (emitted by the new tax pipeline for LOCATION-grouped
  // taxes) carry a lineType; they belong in the per-location render group.
  const invoiceLinesWithLocation = invoiceLines.filter(
    invoiceLine =>
      !('lineType' in invoiceLine) ||
      invoiceLine?.lineType === undefined ||
      invoiceLine?.lineType === null ||
      invoiceLine?.lineType === 'LocationTax'
  )
  invoiceLinesWithLocation.forEach(invoiceLine => {
    // An invoice line with no locationId groups under the "undefined" key, exactly as it did untyped.
    const locationId = String(invoiceLine?.locationId)
    const chargeItem = getChargeItemFromInvoiceLine(organizeInvoiceLineCharges(invoiceLine) as InvoicePreviewLineApi)
    updateLocationIdToChargeObjList(locationIdToChargeObj, seenKeysByLocation, locationId, chargeItem)
  })
  return locationIdToChargeObj
}

const getSubtotalByLocation = (locationIdToChargeList: Record<string, ChargeItem[]>) => {
  const subtotalByLocation: Record<string, number> = {}
  Object.entries(locationIdToChargeList).forEach(([locationId, chargeList]) => {
    subtotalByLocation[locationId] = chargeList.reduce(
      (subtotal, charge) => subtotal + Number.parseFloat(String(charge?.totalWithSubcharges ?? '0')),
      0
    )
  })
  return subtotalByLocation
}

// Invoice-level charges (no parent invoiceLine) — e.g., INVOICE-grouped taxes or invoice-wide fees.
// Reuses the sub-charge shape so the accordion can render them with the same row component.
const getInvoiceLevelCharges = (data: InvoicePreviewApi) => {
  const charges = data?.invoiceFeeTaxCharges ?? []
  return charges.map(charge => getSubChargeItemFromInvoiceFeeTaxCharges(charge, /* pricedServiceType */ ''))
}

export const useFetchChargeList = () => {
  const dispatch = useDispatch()
  const previewPath = useSelector((state: CreateInvoicesRootState) => state.createInvoicesSlice.preview.previewPath)
  const previewDocumentId = useSelector((state: CreateInvoicesRootState) => state.createInvoicesSlice.preview.previewDocumentId)
  const docIdList = useSelector((state: CreateInvoicesRootState) => state.createInvoicesSlice.previewChargeDrawer.docIdList, shallowEqual)
  const prefilledDocIdList = useSelector(
    (state: CreateInvoicesRootState) => state.createInvoicesSlice.preview.prefilledDocIdList,
    shallowEqual
  )
  const [unSubList, setUnSubList] = useState<Unsubscribe[]>([])
  const [fetchedItemsCount, setFetchedItemsCount] = useState(0)

  const updateDocIdToChargeMapItem = ({ docId, data }: { docId: string; data: InvoicePreviewApi }) => {
    const isLoading = (data?.status ?? null) === null
    const totalCents = data?.totalCents ?? null
    const accountNumber = data?.account?.accountNumber
    const accountName = data?.account?.accountName ?? ''
    const total = formatCentsToDollars(totalCents)
    const dueDate = data?.dueDate ?? null
    const locationIdToChargeList = getLocationIdToChargeList(data)
    const subtotalByLocation = getSubtotalByLocation(locationIdToChargeList)
    const lateFees = (data?.lateFees ?? []).map(fee => ({
      ...fee,
      startDate:
        fee.startDate === fee.endDate
          ? getFormattedDate(fee.startDate)
          : [fee.startDate, fee.endDate]
              .filter(Boolean)
              .map(date => getFormattedDate(date))
              .join(' - '),
    }))
    const invoiceLevelCharges = getInvoiceLevelCharges(data)
    const locationListOfChargeList = Object.values(locationIdToChargeList)
    const chargeList = locationListOfChargeList.flat()
    const subchargeLenghtList = chargeList.map(chargeItem => (chargeItem?.subChargeList ?? []).length)
    const chargesCount =
      subchargeLenghtList.reduce((accumulator, subchargeLength) => accumulator + subchargeLength + 1, 0) + invoiceLevelCharges.length
    const poLenghtList = chargeList.map(
      chargeItem => filterValidPO(chargeItem?.workOrders ?? []).length + (chargeItem?.servicePurchaseOrderNumber ? 1 : 0)
    )
    const poCount = calculateSum(poLenghtList)
    dispatch(
      updateDocIdToChargeMap({
        docId,
        value: {
          id: docId,
          total,
          dueDate,
          accountNumber,
          subtotalByLocation,
          locationListOfChargeList,
          invoiceLevelCharges,
          accountName,
          isLoading,
          expanded: false,
          height: 98,
          chargesCount,
          lateFees,
          locationsCount: locationListOfChargeList.length,
          poCount,
        },
      })
    )
  }

  const getNextQuery = (startAfterVal: string | undefined, limitVal: number) => {
    if (startAfterVal) {
      return query(
        collection(db, `${previewPath}/${previewDocumentId}`, 'accounts'),
        orderBy('accountId'),
        limit(limitVal),
        startAfter(startAfterVal)
      )
    }
    return query(collection(db, `${previewPath}/${previewDocumentId}`, 'accounts'), orderBy('accountId'), limit(limitVal))
  }

  const resetState = () => {
    dispatch(resetChargeDrawerState())
    dispatch(resetPreviewData())
    unSubList.forEach(unSub => {
      unSub()
    })
    setFetchedItemsCount(0)
    setUnSubList([])
  }
  const loadMoreRows = async ({ startIndex, stopIndex }: { startIndex: number; stopIndex: number }) => {
    // ignore duplicated request
    if (startIndex in docIdList && stopIndex in docIdList) return Promise.resolve()
    let startAfterVal
    let limitVal
    // initial range subscription
    if (startIndex === 0 && fetchedItemsCount === 0) {
      limitVal = stopIndex - startIndex + 1
      setFetchedItemsCount(limitVal)
    }
    // not initial range subscription
    else if (!(stopIndex in docIdList) && stopIndex >= fetchedItemsCount) {
      startAfterVal = prefilledDocIdList[fetchedItemsCount - 1]
      limitVal = stopIndex - fetchedItemsCount + 1
      setFetchedItemsCount(limitVal)
    }
    // ignore request because is duplicated
    else {
      return Promise.resolve()
    }
    const nextQuery = getNextQuery(startAfterVal, limitVal)
    return new Promise<void>(resolve => {
      const unSub = onSnapshot(
        nextQuery,
        async queryAccountsSnapshot => {
          try {
            const promises = queryAccountsSnapshot
              .docChanges()
              .filter(change => change.type === 'added' || change.type === 'modified')
              .map(async change => {
                const firestoreData = change.doc.data()
                const gcsPath = firestoreData?.gcsPath
                const data = gcsPath ? await getGcsJsonData(gcsPath) : firestoreData
                updateDocIdToChargeMapItem({ docId: change.doc.id, data })
              })
            await Promise.all(promises)
            setUnSubList(prev => [...prev, unSub])
            resolve()
          } catch (error) {
            handleErrorWithMessage(error, T.INVOICE_PREVIEW_LOAD_ERROR)
            resetState()
            resolve()
          }
        },
        error => {
          resetState()
          handleErrorWithMessage(error, T.INVOICE_PREVIEW_LOAD_ERROR)
        }
      )
    })
  }

  useEffect(() => {
    resetState()
    return () => {
      resetState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount and unmount only; resetState is rebuilt on every render
  }, [])

  return { loadMoreRows, onClose: resetState, isLoadingFirstBatch: fetchedItemsCount === 0 }
}
